import { LitElement, html, nothing } from 'lit';
import fsTypoStyles from '@cof/fs-typography-core/lib/typography.lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './step-income.style.js';
import { ApplicationService } from '../../../services/application.service.js';
import './job/job.component.js';
import './income/income.component.js';
import { globalStyle } from '../../../global-styles.style.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';
import { stratum } from '../../../../constants/stratum.js';

class RefiStepIncomeComponent extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiStepIncome');
    this.application = {};
    this.validity = {};
    this.showAdditionalIncome = false;
    this.childIds = {
      job: 'job-input',
      income: 'income-input',
      additionalIncome: 'additional-income-input',
    };
  }

  static properties = {
    application: { type: Object, reflect: true },
    applicant: { type: Object, reflect: true },
    isPrefill: { type: Boolean, attribute: 'is-prefill' },
    isPrimary: { type: Boolean, attribute: 'is-primary', reflect: true },
    showAdditionalIncome: { type: Boolean, reflect: true },
  };

  static styles = [style, globalStyle, fsTypoStyles];

  connectedCallback() {
    super.connectedCallback();
    this.application = ApplicationService.Application;
    this.applicant = this.isPrimary
      ? this.application.primaryApplicant
      : this.application.coApplicant;
    this.coAppPrefix = this.isPrimary ? '' : 'co-';
    this.showAdditionalIncome = !!this.applicant.otherIncome ?? false;
    stratum.publishTag(
      this.isPrimary
        ? TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_INCOME
        : TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL_CO_APP_INCOME
    );
  }

  render() {
    return html`
      <div>
        <form
          id="step-income-form"
          class="step-income-form"
          @submit="${this.onSubmit}"
          novalidate
        >
          <refi-heading>
            ${this.isPrimary
              ? html`What is your income?`
              : html`What is ${this.applicant.firstName}’s income?`}
          </refi-heading>

          ${this.applicant.employmentStatus === 'employed' ||
          this.applicant.employmentStatus === 'selfemployed'
            ? html` <refi-personal-job
                id="${this.childIds.job}"
                @validity="${this.updateValidity}"
                ?is-primary="${this.isPrimary}"
              ></refi-personal-job>`
            : nothing}
          <refi-personal-income
            id="${this.childIds.income}"
            @validity="${this.updateValidity}"
            ?is-primary="${this.isPrimary}"
          >
          </refi-personal-income>

          <a
            class="step-income__add-additional-source fs-link-standalone fs-link-standalone--normal"
            href="#"
            @click="${(e) => {
              this.toggleAdditionalIncome(e);
            }}"
            @keydown="${(e) => {
              if (e.key === 'Enter') {
                this.toggleAdditionalIncome(e);
              }
            }}"
            tabindex="0"
            role="button"
            attr.aria-expanded="${this.showAdditionalIncome}"
          >
            ${this.showAdditionalIncome
              ? html`- Remove additional income source`
              : html`+ Add another source of income`}
          </a>

          ${this.showAdditionalIncome
            ? html` <refi-personal-income
                id="${this.childIds.additionalIncome}"
                additional-amount
                @validity="${this.updateValidity}"
                ?is-primary="${this.isPrimary}"
              ></refi-personal-income>`
            : nothing}

          <fs-body-text size="small" class="callout">
            You don’t have to include alimony, child support, or separate
            maintenance income unless you want to use it to repay your loan.
          </fs-body-text>
          <fs-body-text
            size="small"
            class="callout"
            ?hidden=${this.applicant.employmentStatus !== 'selfemployed'}
          >
            Please ensure your personal income documents (bank statements, W2,
            etc.) support this stated income.
          </fs-body-text>

          <div class="button-container">
            <fs-button class="fs-button next-button" variant="primary"
              >Next</fs-button
            >
          </div>
        </form>
      </div>
    `;
  }

  toggleAdditionalIncome(ev) {
    ev.preventDefault();
    this.showAdditionalIncome = !this.showAdditionalIncome;
    delete this.validity.incomeAmountOther;
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
  }

  @validateForm()
  async onSubmit() {
    let valid = true;
    this.debug.log(this.validity);
    Object.values(this.validity).forEach((validityValue) => {
      if (!validityValue) {
        valid = false;
      }
    });

    if (valid) {
      this.inputChildren = {
        job: this.shadowRoot.getElementById(this.childIds.job),
        income: this.shadowRoot.getElementById(this.childIds.income),
        additionalIncome: this.shadowRoot.getElementById(
          this.childIds.additionalIncome
        ),
      };

      this.oldApplicantInfo = JSON.parse(JSON.stringify(this.applicant));
      this.oldApplicantEmploymentInfo = JSON.parse(
        JSON.stringify(this.applicant.employmentInfo)
      );

      Object.values(this.inputChildren).forEach((child) => {
        if (child) {
          Object.keys(child.values).forEach((field) => {
            if (field === 'jobDescription') {
              this.applicant.employmentInfo[field] = child.values[field];
            } else {
              this.applicant[field] = child.values[field];
            }
          });
        }
      });

      if (!this.showAdditionalIncome) {
        this.applicant.otherIncome = null;
      }
      stratum.publishTag(TagKey.PERSONAL_INCOME_ADD_ADDITIONAL_INCOME, {
        replacements: { value: this.showAdditionalIncome.toString() },
      });
      this.tagFieldChangesOnSubmit();
      this.dispatchEvent(new CustomEvent('next-step'));
    }
  }

  tagFieldChangesOnSubmit() {
    Object.keys(this.applicant).forEach((field) => {
      if (
        typeof this.applicant[field] !== 'object' &&
        this.applicant[field] !== this.oldApplicantInfo[field]
      ) {
        this.debug.log(`${this.coAppPrefix}${field}`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${this.coAppPrefix}${field}`,
          },
        });
      }
    });
    Object.keys(this.applicant.employmentInfo).forEach((field) => {
      if (
        this.applicant.employmentInfo[field] !==
        this.oldApplicantEmploymentInfo[field]
      ) {
        this.debug.log(`${this.coAppPrefix}${field}`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${this.coAppPrefix}${field}`,
          },
        });
      }
    });
  }
}

customElements.define('refi-step-income', RefiStepIncomeComponent);
